import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'
import { transformCurriculumCourseTree } from 'layers/content/v3/transforms/transformCurriculumCourseTree'
import {
  operations as curriculumCourseOperations,
  selectors as curriculumCourseSelectors,
} from 'store/curriculumCourseManager'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import { findNode } from 'utils/highschoolHelpers'
import { isPreviewRoute } from 'utils/routing/routeHelpers'

/**
 * Normalises a coursePointer.query object that may use either camelCase keys
 * (curriculum-service schema) or snake_case keys (wire format).
 */
function parseCoursePointerQuery(coursePointer) {
  const query = coursePointer?.query || {}
  return {
    courseCode: query.courseCode || query.course_code || null,
  }
}

/**
 * Resolves the active siteId from an explicit value, userContext, or URL param.
 */
function resolveSiteId({ explicitSiteId, userContext }) {
  if (explicitSiteId != null) return explicitSiteId
  const fromContext = userContext?.sites?.[0]?.siteId
  if (fromContext != null) return fromContext
  const search =
    typeof window !== 'undefined' ? window?.location?.search || '' : ''
  const query = new URLSearchParams(search)
  const fromUrl = query.get('siteId') || query.get('site')
  if (!fromUrl) return null
  const parsed = Number(fromUrl)
  return Number.isNaN(parsed) ? null : parsed
}

function findLeafNodeIdFromLessonNodes(lessonNodes, courseNodeId) {
  if (!Array.isArray(lessonNodes) || !courseNodeId) return null
  const match = lessonNodes.find(node => {
    if (node?.course_node_id === courseNodeId) return true
    const leafId = node?.leaf_node_id
    return typeof leafId === 'string' && leafId.startsWith(courseNodeId)
  })
  return match?.leaf_node_id || null
}

function checkIsComplete({
  registrations,
  registrationId,
  courseNodeId,
  leafNodeId,
}) {
  if (!Array.isArray(registrations) || !courseNodeId) return false
  const match =
    registrationId != null
      ? registrations.find(
          reg => String(reg.registration_id) === String(registrationId),
        )
      : null
  if (!match || !Array.isArray(match.lesson_nodes)) return false
  return match.lesson_nodes.some(node => {
    if (node.course_node_id === courseNodeId) return !!node.is_done
    if (leafNodeId && node.leaf_node_id === leafNodeId) return !!node.is_done
    return false
  })
}

function getRegistrations(instructorData) {
  if (!instructorData) return []
  if (Array.isArray(instructorData)) return instructorData
  if (typeof instructorData !== 'object') return []

  // The instructor/me payload can expose both `siteEducator` and `educator`
  // registration arrays. Include both so downstream completion/leaf-node lookups
  // can resolve a registration regardless of which bucket it lives in.
  return Object.values(instructorData).flatMap(scope => {
    if (!scope) return []
    if (Array.isArray(scope)) return scope
    if (typeof scope !== 'object') return []
    if (typeof scope.registration_id !== 'undefined') return [scope]
    return Object.values(scope).flatMap(value => {
      if (!value) return []
      if (Array.isArray(value)) return value
      if (
        typeof value === 'object' &&
        typeof value.registration_id !== 'undefined'
      ) {
        return [value]
      }
      return []
    })
  })
}

/**
 * Resolves which registration id to send to the attempts endpoint.
 *
 * The `class` query param is only the registration id for `siteEducator`
 * registrations. For `educator` registrations the `class` param does not map to a
 * registration id, so we instead resolve it by cross-checking the
 * curriculum-service course code against the instructor/me payload:
 *  - If the course code matches a `siteEducator` registration, keep the existing
 *    flow and use the `class` query param as the registration id.
 *  - If the course code matches an `educator` registration (and not a
 *    `siteEducator` one), use the first matching educator registration's
 *    registration id instead.
 */
function resolveRegistrationId({
  instructorData,
  registrations,
  classId,
  courseCode,
  instructorDataKey,
}) {
  if (!instructorData) return null

  const normalizedCourseCode = String(courseCode || '').toLowerCase()
  const matchesCourseCode = registration => {
    if (!normalizedCourseCode) return false
    const registrationCourseCode = String(
      registration?.course_name || registration?.course_code || '',
    ).toLowerCase()
    return registrationCourseCode === normalizedCourseCode
  }

  const siteEducatorRegistrations = Array.isArray(instructorData.siteEducator)
    ? instructorData.siteEducator
    : []
  const educatorRegistrations = Array.isArray(instructorData.educator)
    ? instructorData.educator
    : []

  const siteEducatorCourseMatch = siteEducatorRegistrations.find(
    matchesCourseCode,
  )
  const educatorCourseMatch = educatorRegistrations.find(matchesCourseCode)

  // The course code resolves to an educator registration: the `class` query
  // param is not its registration id, so use the matched educator registration's
  // id. siteEducator matches take precedence to preserve the existing flow.
  if (educatorCourseMatch && !siteEducatorCourseMatch) {
    return educatorCourseMatch.registration_id ?? null
  }

  // siteEducator / default flow: the `class` query param is the registration id.
  if (!classId) return null
  const match = registrations.find(
    registration => String(registration.registration_id) === String(classId),
  )
  if (!match) {
    console.error(
      `[useMarkCompleteV3] no registration found for registration_id "${classId}" (instructorDataKey: "${instructorDataKey}")`,
    )
    return null
  }
  return match.registration_id ?? null
}

/**
 * Encapsulates curriculum-service based mark-complete behavior for
 * IndividualResourceV3 routes.
 */
export function useMarkCompleteV3({
  coursePointer,
  courseNodeId,
  contentGroupCode,
  instructorDataKey,
  siteId: explicitSiteId = null,
}) {
  const dispatch = useDispatch()
  const userContext = useUserContext()
  const location = useLocation()

  const { courseCode } = useMemo(() => parseCoursePointerQuery(coursePointer), [
    coursePointer,
  ])

  const siteId = useMemo(() => resolveSiteId({ explicitSiteId, userContext }), [
    explicitSiteId,
    userContext,
  ])

  const isFetchingOrCreating = useSelector(
    state => !!state.lmsManager?.isFetchingOrCreatingProgramFlex,
  )
  const instructorData = useSelector(
    lmsSelectors.selectInstructorData(instructorDataKey),
  )

  const hasAttemptedInstructorFetch = useRef(false)

  useEffect(() => {
    if (isFetchingOrCreating || instructorData) return
    if (isPreviewRoute()) return
    if (hasAttemptedInstructorFetch.current) return
    hasAttemptedInstructorFetch.current = true
    dispatch(lmsOperations.fetchInstructorProgramFlexGet())
  }, [dispatch, isFetchingOrCreating, instructorData])

  const classId = useMemo(() => {
    const search =
      location?.search ||
      (typeof window !== 'undefined' ? window?.location?.search || '' : '')
    const query = new URLSearchParams(search)
    return query.get('class')
  }, [location?.search])

  const registrations = useMemo(() => getRegistrations(instructorData), [
    instructorData,
  ])

  const registrationCourseCode = useMemo(() => {
    if (!classId) return null
    return (
      registrations.find(reg => String(reg.registration_id) === String(classId))
        ?.course_name || null
    )
  }, [registrations, classId])

  const courseCodeForTree = courseCode || registrationCourseCode || null
  const courseTreeFetchKey =
    courseCodeForTree && siteId != null
      ? `${contentGroupCode || ''}|${courseCodeForTree}|${String(siteId)}`
      : null
  const lastAttemptedCourseTreeFetchKey = useRef(null)

  const courseTreeEntry = useSelector(
    curriculumCourseSelectors.selectCurriculumCourseTree(
      contentGroupCode,
      courseCodeForTree,
      siteId,
    ),
  )

  useEffect(() => {
    if (!coursePointer) return
    if (!courseCodeForTree || siteId == null) return
    if (courseTreeEntry?.data || courseTreeEntry?.loading) return
    if (
      courseTreeFetchKey &&
      lastAttemptedCourseTreeFetchKey.current === courseTreeFetchKey
    ) {
      return
    }
    lastAttemptedCourseTreeFetchKey.current = courseTreeFetchKey
    dispatch(
      curriculumCourseOperations.fetchCourseTreeFromPointer({
        coursePointer,
        contentGroupCode,
        siteId,
      }),
    )
  }, [
    coursePointer,
    courseCodeForTree,
    courseTreeFetchKey,
    contentGroupCode,
    siteId,
    courseTreeEntry?.data,
    courseTreeEntry?.loading,
    dispatch,
  ])

  const treeLeafNodeId = useMemo(() => {
    if (!courseTreeEntry?.data || !courseNodeId) return null
    const transformedTree = transformCurriculumCourseTree(courseTreeEntry.data)
    if (!transformedTree) return null
    const node = findNode(transformedTree, courseNodeId)
    if (!node?.leafNodeId) {
      console.error(
        `[useMarkCompleteV3] courseNodeId "${courseNodeId}" not found in course tree (contentGroupCode: "${contentGroupCode}")`,
      )
      return null
    }
    return node.leafNodeId
  }, [courseTreeEntry, courseNodeId, contentGroupCode])

  const registrationId = useMemo(
    () =>
      resolveRegistrationId({
        instructorData,
        registrations,
        classId,
        courseCode,
        instructorDataKey,
      }),
    [instructorData, registrations, classId, courseCode, instructorDataKey],
  )

  const fallbackLeafNodeId = useMemo(() => {
    if (!courseNodeId) return null
    if (!registrations.length) return null
    const matchedRegistration =
      registrationId != null
        ? registrations.find(
            reg => String(reg.registration_id) === String(registrationId),
          )
        : null

    if (matchedRegistration) {
      return findLeafNodeIdFromLessonNodes(
        matchedRegistration.lesson_nodes,
        courseNodeId,
      )
    }

    for (const registration of registrations) {
      const foundLeafNodeId = findLeafNodeIdFromLessonNodes(
        registration.lesson_nodes,
        courseNodeId,
      )
      if (foundLeafNodeId) return foundLeafNodeId
    }
    return null
  }, [registrations, registrationId, courseNodeId])

  const leafNodeId = treeLeafNodeId || fallbackLeafNodeId

  const isComplete = useMemo(
    () =>
      checkIsComplete({
        registrations,
        registrationId,
        courseNodeId,
        leafNodeId,
      }),
    [registrations, registrationId, courseNodeId, leafNodeId],
  )

  const handleMarkComplete = async () => {
    if (isComplete) return
    if (!registrationId || !leafNodeId) {
      if (
        registrationId &&
        !leafNodeId &&
        coursePointer &&
        !courseTreeEntry?.loading
      ) {
        await dispatch(
          curriculumCourseOperations.fetchCourseTreeFromPointer({
            coursePointer,
            contentGroupCode,
            siteId,
          }),
        )
      }
      console.error(
        '[useMarkCompleteV3] registrationId and leafNodeId are required to mark complete',
      )
      return
    }

    await dispatch(
      lmsOperations.makeAttemptV2Direct({
        registrationId,
        leafNodeId,
        progress: 1,
      }),
    )
    await dispatch(lmsOperations.fetchInstructorProgramFlexGet())
  }

  return {
    isComplete,
    handleMarkComplete,
    registrationId,
    leafNodeId,
  }
}
